import { useCallback, useMemo, useState } from 'react';
import { Box, DataTable, DataTableBody } from '@optiaxiom/react';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';

import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { SIDEBAR_WIDTH } from '@frontend/styles/layout.css';
import { Tool } from '@frontend/types';

import createColumns from './columns';
import { ToolRowDetailsSidebar } from './ToolRowDetailsSidebar';
import * as styles from './ToolsTable.css';
import { ToolsTableRow } from './types';

type ToolsTableProps = {
  tools: Tool[];
  isUpdating: boolean;
  updatingToolId: string | null | undefined;
  isUpdatingEnabledInPrimaryAgent: boolean;
  updatingToolIdEnabledInPrimaryAgent: string | null | undefined;
  onToggleActive: (toolId: string, toolName: string, isDisabled: boolean) => void;
  onToggleEnabledInPrimaryAgent: (toolName: string, isEnabledInPrimaryAgent: boolean) => void;
  onBulkToggle: (serviceId: string, functionIds: string[], action: 'enable' | 'disable') => void;
  isBulkToggling: boolean;
  onBulkToggleChatEnabled: (toolNames: string[], action: 'enable' | 'disable') => void;
  isBulkTogglingChat: boolean;
  onRestoreToolsClick?: () => void;
};

export function ToolsTable({
  tools,
  isUpdating,
  updatingToolId,
  isUpdatingEnabledInPrimaryAgent,
  updatingToolIdEnabledInPrimaryAgent,
  onToggleActive,
  onToggleEnabledInPrimaryAgent,
  onBulkToggle,
  isBulkToggling,
  onBulkToggleChatEnabled,
  isBulkTogglingChat,
  onRestoreToolsClick,
}: ToolsTableProps) {
  const enabledToolCount = useMemo(
    () => tools.filter(tool => tool.is_enabled_in_primary_agent).length,
    [tools]
  );
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);
  const selectedTool = useMemo(
    () => tools.find(tool => tool.function_id === selectedToolId) ?? null,
    [tools, selectedToolId]
  );
  const handleRowSelection = useCallback((tool: Tool | null) => {
    setSelectedToolId(tool?.function_id ?? null);
  }, []);

  const columns = useMemo(
    () =>
      createColumns({
        enabledToolCount,
        isUpdating,
        updatingToolId,
        isUpdatingEnabledInPrimaryAgent,
        updatingToolIdEnabledInPrimaryAgent,
        onToggleActive,
        onToggleEnabledInPrimaryAgent,
        onBulkToggle,
        isBulkToggling,
        onBulkToggleChatEnabled,
        isBulkTogglingChat,
        onRestoreToolsClick,
        handleRowSelection,
      }),
    [
      enabledToolCount,
      isUpdating,
      updatingToolId,
      isUpdatingEnabledInPrimaryAgent,
      updatingToolIdEnabledInPrimaryAgent,
      onToggleActive,
      onToggleEnabledInPrimaryAgent,
      onBulkToggle,
      isBulkToggling,
      onBulkToggleChatEnabled,
      isBulkTogglingChat,
      onRestoreToolsClick,
      handleRowSelection,
    ]
  );

  const data = useMemo(() => {
    const registryMap = new Map<string, ToolsTableRow>();
    tools.forEach(tool => {
      const registryId = tool.service.id;
      if (!registryMap.has(registryId)) {
        registryMap.set(registryId, {
          id: tool.service.id,
          name: tool.service.name,
          isRegistry: true,
          registry: tool.service,
          items: [
            {
              id: tool.function_id,
              name: tool.name,
              isRegistry: false,
              auth_status: tool.auth_status,
              tool: tool,
              items: [],
            },
          ],
        });
        return;
      }
      registryMap.get(registryId)?.items.push({
        id: tool.function_id,
        name: tool.name,
        isRegistry: false,
        auth_status: tool.auth_status,
        tool: tool,
        items: [],
      });
    });

    return Array.from(registryMap.values());
  }, [tools]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.items,
  });

  return (
    <>
      <Box className={selectedTool ? styles.tableContainerWithSidebar : styles.tableContainer}>
        <DataTable table={table}>
          <DataTableBody />
        </DataTable>
      </Box>
      <Sidebar open={selectedTool !== null} dock="right" initialWidth={SIDEBAR_WIDTH}>
        {selectedTool && (
          <ToolRowDetailsSidebar
            selectedTool={selectedTool}
            onClose={() => handleRowSelection(null)}
            onEnabledInChatChange={enabled =>
              onToggleEnabledInPrimaryAgent(selectedTool.name, !enabled)
            }
            onToggleActive={onToggleActive}
          />
        )}
      </Sidebar>
    </>
  );
}
